import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Gift, Loader2 } from 'lucide-react'
import { apiClient } from '@/api/client'
import { cn } from '@/lib/utils'

/**
 * F-MM-N24 — apply a gift card balance to the bill.
 *
 * Two steps on purpose: look the card up (which moves nothing), then confirm
 * an amount against a balance the cashier can see. The old /scan flow asked
 * for an amount with the balance on a different screen, which is how a card
 * got over-drawn and the guest was told at the counter.
 *
 * Uses the shared Dialog rather than a bespoke overlay: the checkout rail
 * sits inside a backdrop-blur wrapper, which creates a containing block for
 * `position: fixed` and clips anything that isn't portalled to the body.
 *
 * This component never subtracts anything itself. It reports the amount the
 * cashier chose; the balance that lands on the receipt is the one the server
 * returns from the card's append-only ledger.
 */

export type GiftCardPreview = {
  id: string
  code: string
  balance: number
  customer_name: string
  redeemable: boolean
  blocker: string
}

export interface GiftCardTenderDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Amount still owing on the bill — the ceiling for this tender. */
  remaining: number
  formatCurrency: (amount: number) => string
  /** Hands the parent the card and the amount to draw off it. */
  onApply: (code: string, amount: number) => void
  /** A payment mutation is in flight — gates the confirm button. */
  pending?: boolean
}

export function GiftCardTenderDialog({
  open,
  onOpenChange,
  remaining,
  formatCurrency,
  onApply,
  pending,
}: GiftCardTenderDialogProps) {
  const [code, setCode] = useState('')
  const [preview, setPreview] = useState<GiftCardPreview | null>(null)
  const [amount, setAmount] = useState('')
  const [lookupBusy, setLookupBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset on every open: a card left on screen from the previous guest is a
  // card somebody eventually charges by accident.
  useEffect(() => {
    if (!open) return
    setCode('')
    setPreview(null)
    setAmount('')
    setError(null)
    setLookupBusy(false)
  }, [open])

  const lookup = async () => {
    const raw = code.trim()
    if (!raw) return
    setLookupBusy(true)
    setError(null)
    try {
      const res = await apiClient.previewGiftCard(raw)
      if (!res.success || !res.data) {
        setError(res.message || 'No gift card with that code')
        setPreview(null)
        return
      }
      setPreview(res.data)
      if (res.data.redeemable) {
        // Default to whichever runs out first: the card or the bill. The
        // common case is then a single tap, and the cashier never types an
        // amount that is about to be refused.
        setAmount(String(Math.min(res.data.balance, remaining)))
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not look that card up')
      setPreview(null)
    } finally {
      setLookupBusy(false)
    }
  }

  const parsed = Number(amount.trim())
  const amountValid = amount.trim() !== '' && Number.isFinite(parsed) && parsed > 0
  const overBalance = Boolean(preview && amountValid && parsed > preview.balance)
  const overBill = amountValid && parsed > remaining
  const canApply = Boolean(preview?.redeemable) && amountValid && !overBalance && !overBill && !pending

  const leftAfter = preview && amountValid ? Math.max(0, preview.balance - parsed) : null
  const billLeftAfter = amountValid ? Math.max(0, remaining - parsed) : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-4 w-4" aria-hidden />
            Pay with a gift card
          </DialogTitle>
          <DialogDescription>
            {formatCurrency(remaining)} still owing on this bill.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="gift-tender-code" className="text-sm font-medium">
              Card code
            </label>
            <div className="flex gap-2">
              <Input
                id="gift-tender-code"
                autoFocus
                placeholder="GC-A1B2C3D4"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value)
                  setPreview(null)
                  setError(null)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && code.trim() && !lookupBusy) {
                    e.preventDefault()
                    void lookup()
                  }
                }}
                className="h-10 flex-1 font-mono"
              />
              <Button
                type="button"
                variant="secondary"
                className="h-10 shrink-0"
                disabled={!code.trim() || lookupBusy}
                onClick={() => void lookup()}
              >
                {lookupBusy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : 'Check'}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Checking a card takes nothing off it.
            </p>
          </div>

          {error ? (
            <p role="alert" className="text-sm font-medium text-destructive">
              {error}
            </p>
          ) : null}

          {preview ? (
            <div
              className={cn(
                'rounded-lg border p-3',
                preview.redeemable
                  ? 'border-emerald-200 bg-emerald-50/70 dark:border-emerald-900/60 dark:bg-emerald-950/40'
                  : 'border-border bg-muted/40'
              )}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  Balance
                </span>
                <span className="text-lg font-semibold tabular-nums">
                  {formatCurrency(preview.balance)}
                </span>
              </div>
              {preview.customer_name ? (
                <div className="mt-1 text-xs text-muted-foreground">{preview.customer_name}</div>
              ) : null}
              {!preview.redeemable && preview.blocker ? (
                <p className="mt-2 text-sm font-medium text-destructive">{preview.blocker}</p>
              ) : null}
            </div>
          ) : null}

          {preview?.redeemable ? (
            <div className="space-y-1.5">
              <label htmlFor="gift-tender-amount" className="text-sm font-medium">
                Take off this card
              </label>
              <Input
                id="gift-tender-amount"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canApply) {
                    e.preventDefault()
                    onApply(preview.code, parsed)
                  }
                }}
                className="h-11 text-base tabular-nums"
              />
              {overBalance ? (
                <p role="alert" className="text-sm font-medium text-destructive">
                  Only {formatCurrency(preview.balance)} left on this card.
                </p>
              ) : overBill ? (
                <p role="alert" className="text-sm font-medium text-destructive">
                  That is more than the {formatCurrency(remaining)} still owing.
                </p>
              ) : amountValid ? (
                <p className="text-[11px] text-muted-foreground">
                  {billLeftAfter && billLeftAfter > 0
                    ? `${formatCurrency(billLeftAfter)} left to pay by cash, card or online. `
                    : 'Settles the bill in full. '}
                  {leftAfter !== null
                    ? leftAfter > 0
                      ? `${formatCurrency(leftAfter)} stays on the card.`
                      : 'The card is used up.'
                    : ''}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canApply}
            onClick={() => {
              if (preview && canApply) onApply(preview.code, parsed)
            }}
          >
            {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}
            {amountValid ? `Take off ${formatCurrency(parsed)}` : 'Take off'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
